import { Directive, Input, HostListener, ElementRef, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';

@Directive({
    selector: '[phoneMask]',
    standalone: false
})
export class PhoneMaskDirective implements OnInit {
    @Input() phoneControl: AbstractControl;
    private phoneInput: HTMLInputElement;
    private previousValue: string = '';
    private cursorPosition: number = 0;
    private readonly maskPattern = '(XXX) XXX-XXXX'; // Placeholder 'X' instead of '0'

    constructor(private el: ElementRef) {
        this.phoneInput = this.el.nativeElement;
    }

    ngOnInit(): void {
        if (this.phoneControl) {
            this.phoneControl.valueChanges.subscribe(value => {
                if (value !== this.previousValue) {
                    this.applyMask(value);
                }
            });
        }
    }

    @HostListener('input', ['$event'])
    onInput(event: any): void {
        let cursor = this.phoneInput.selectionStart || 0;
        let rawValue = event.target.value.replace(/\D/g, ''); // Keep only numbers
        this.applyMask(rawValue, cursor);
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        let pasteData = (event.clipboardData || window['clipboardData'])?.getData('text') ?? '';

        // Extract only digits from pasted text
        let digitsOnly = pasteData.replace(/\D/g, '');

        // Handle selection range replacement
        let selectionStart = this.phoneInput.selectionStart || 0;
        let selectionEnd = this.phoneInput.selectionEnd || 0;
        let currentRawValue = this.phoneInput.value.replace(/\D/g, '');

        if (selectionStart !== selectionEnd) {
            // Replace selected range with pasted digits
            let startIndex = this.getRawIndex(selectionStart);
            let endIndex = this.getRawIndex(selectionEnd);
            let newRawValue = currentRawValue.slice(0, startIndex) + digitsOnly + currentRawValue.slice(endIndex);
            this.applyMask(newRawValue, selectionStart, false, true);
        } else {
            // Insert at cursor position or replace all if no selection
            if (this.phoneInput.value === '' || digitsOnly.length >= 10) {
                // Replace entire field if empty or pasting full number
                this.applyMask(digitsOnly, 0, false, true);
            } else {
                // Insert at cursor position
                let cursorIndex = this.getRawIndex(selectionStart);
                let newRawValue = currentRawValue.slice(0, cursorIndex) + digitsOnly + currentRawValue.slice(cursorIndex);
                this.applyMask(newRawValue, selectionStart, false, true);
            }
        }

        // Set cursor to end of pasted content
        setTimeout(() => {
            let endPosition = this.phoneInput.value.length;
            this.phoneInput.setSelectionRange(endPosition, endPosition);
        }, 0);
    }

    @HostListener('copy', ['$event'])
    onCopy(event: ClipboardEvent): void {
        let selectionStart = this.phoneInput.selectionStart || 0;
        let selectionEnd = this.phoneInput.selectionEnd || 0;

        if (selectionStart !== selectionEnd) {
            // Copy the formatted text for partial selection
            let selectedText = this.phoneInput.value.substring(selectionStart, selectionEnd);
            event.clipboardData?.setData('text/plain', selectedText);
            event.preventDefault();
        } else if (selectionStart === 0 && selectionEnd === this.phoneInput.value.length) {
            // Copy full phone number - ensure we copy the complete formatted number
            event.clipboardData?.setData('text/plain', this.phoneInput.value);
            event.preventDefault();
        }
    }

    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        this.cursorPosition = this.phoneInput.selectionStart || 0;

        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();
            this.handleDelete(event.key);
        }

        // Handle Ctrl+A / Cmd+A (select all)
        if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
            // Allow default behavior for select all
            return;
        }
    }
    private applyMask(value: string, cursor: number = this.cursorPosition, isReplacement: boolean = false, emitEvent: boolean = false): void {
        value = value ?? '';
        let formattedValue = this.formatNumber(value);
        let newCursorPosition = isReplacement
            ? this.getCursorAfterReplace(cursor, formattedValue)
            : this.getUpdatedCursorPosition(cursor, formattedValue);

        this.phoneInput.value = formattedValue;
        this.phoneInput.setSelectionRange(newCursorPosition, newCursorPosition);
        this.previousValue = formattedValue;

        if (this.phoneControl) {
            this.phoneControl.setValue(formattedValue, { emitEvent: emitEvent });
        }
    }

    private getCursorAfterReplace(oldCursor: number, formattedValue: string): number {
        let newCursor = oldCursor;

        while (newCursor < formattedValue.length && !/\d/.test(formattedValue[newCursor])) {
            newCursor++;
        }

        return Math.min(newCursor, formattedValue.length);
    }

    private formatNumber(value: string): string {
        value = value ?? '';
        let numbers = value.substring(0, 10); // Max 10 digits
        let formatted = '';
        let maskArray = this.maskPattern.split('');

        let numberIndex = 0;
        for (let i = 0; i < maskArray.length; i++) {
            if (maskArray[i] === 'X' && numberIndex < numbers.length) {
                formatted += numbers[numberIndex];
                numberIndex++;
            } else if (maskArray[i] !== 'X') {
                formatted += (numberIndex < numbers.length) ? maskArray[i] : ''; // Prevent prefilled formatting
            }
        }

        return formatted;
    }

    private getUpdatedCursorPosition(oldCursor: number, formattedValue: string): number {
        let newCursor = oldCursor;

        while (newCursor < formattedValue.length && !/\d/.test(formattedValue[newCursor])) {
            newCursor++;
        }

        return Math.min(newCursor, formattedValue.length);
    }

    private handleDelete(key: string): void {
        let rawValue = this.phoneInput.value.replace(/\D/g, '');
        let selectionStart = this.phoneInput.selectionStart || 0;
        let selectionEnd = this.phoneInput.selectionEnd || 0;

        // Handle selection range deletion
        if (selectionStart !== selectionEnd) {
            let startIndex = this.getRawIndex(selectionStart);
            let endIndex = this.getRawIndex(selectionEnd);
            rawValue = rawValue.slice(0, startIndex) + rawValue.slice(endIndex);
            this.applyMask(rawValue, selectionStart);
            return;
        }

        // Handle single character deletion
        let cursor = selectionStart;
        let deleteIndex = this.getRawIndex(cursor);
        if (key === 'Backspace' && deleteIndex > 0) {
            rawValue = rawValue.slice(0, deleteIndex - 1) + rawValue.slice(deleteIndex);
            cursor--;
        } else if (key === 'Delete' && deleteIndex < rawValue.length) {
            rawValue = rawValue.slice(0, deleteIndex) + rawValue.slice(deleteIndex + 1);
        }

        this.applyMask(rawValue, cursor);
    }

    private getRawIndex(cursor: number): number {
        let digitIndex = 0;

        for (let i = 0; i < this.phoneInput.value.length; i++) {
            if (/\d/.test(this.phoneInput.value[i])) {
                if (i >= cursor) {
                    return digitIndex;
                }
                digitIndex++;
            }
        }
        return digitIndex;
    }

}
